import React, { useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import css from './FieldMultipleSelect.module.css';

// Icono Chevron Down (inline SVG)
const ChevronDownIcon = ({ size = 20, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

// Icono X (inline SVG)
const XIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const MultiSelectInput = ({
  input,
  meta,
  id,
  label,
  options = [],
  placeholder,
  disabled = false,
  showLabelAsDisabled = false,
  className = '',
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const selectedValues = Array.isArray(input.value) ? input.value : [];

  const handleToggle = () => {
    if (!disabled) setIsOpen(v => !v);
  };

  const handleSelect = optionValue => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];

    input.onChange(newValues);
  };

  const handleRemove = (optionValue, e) => {
    e.stopPropagation();
    input.onChange(selectedValues.filter(v => v !== optionValue));
  };

  const selectedLabels = options
    .filter(opt => selectedValues.includes(opt.value))
    .map(opt => ({ value: opt.value, label: opt.label }));

  const rootClasses = classNames(css.root, className);
  const labelClasses = classNames(css.label, {
    [css.labelDisabled]: disabled || showLabelAsDisabled,
  });

  const controlClasses = classNames(css.control, {
    [css.controlOpen]: isOpen,
    [css.controlDisabled]: disabled,
  });

  const chevronClasses = classNames(css.chevron, {
    [css.chevronOpen]: isOpen,
  });

  return (
    <div className={rootClasses}>
      {label ? (
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
      ) : null}

      <div className={css.wrapper}>
        <div
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') handleToggle();
          }}
          className={controlClasses}
          aria-disabled={disabled}
          aria-expanded={isOpen}
        >
          <div className={css.pills}>
            {selectedLabels.length === 0 ? (
              <span className={css.placeholder}>{placeholder}</span>
            ) : (
              selectedLabels.map(item => (
                <span key={item.value} className={css.pill}>
                  <span className={css.pillLabel}>{item.label}</span>

                  {!disabled ? (
                    <button
                      type="button"
                      className={css.removeBtn}
                      onClick={e => handleRemove(item.value, e)}
                      aria-label={`Quitar ${item.label}`}
                    >
                      <XIcon size={14} />
                    </button>
                  ) : null}
                </span>
              ))
            )}
          </div>

          <ChevronDownIcon size={20} className={chevronClasses} />
        </div>

        {isOpen && !disabled ? (
          <>
            <div className={css.backdrop} onClick={() => setIsOpen(false)} />

            <div className={css.dropdown}>
              {options.length === 0 ? (
                <div className={css.empty}>{intl.formatMessage({ id: 'FieldMultipleSelect.empty' })}</div>
              ) : (
                options.map(option => {
                  const selected = selectedValues.includes(option.value);
                  const optionClasses = classNames(css.option, {
                    [css.optionSelected]: selected,
                  });

                  const checkboxClasses = classNames(css.checkbox, {
                    [css.checkboxSelected]: selected,
                  });

                  return (
                    <div
                      key={option.value}
                      className={optionClasses}
                      onClick={() => handleSelect(option.value)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') handleSelect(option.value);
                      }}
                    >
                      <div className={checkboxClasses} aria-hidden="true">
                        {selected ? (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="white"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        ) : null}
                      </div>

                      <span className={css.optionLabel}>{option.label}</span>
                    </div>
                  );
                })
              )}
            </div>
          </>
        ) : null}
      </div>

      {selectedLabels.length > 0 ? (
        <p className={css.counter}>
          {intl.formatMessage({ id: 'FieldMultipleSelect.selectedContacts' }, { count: selectedLabels.length })}
        </p>
      ) : null}

      {meta.touched && meta.error ? <div className={css.error}>{meta.error}</div> : null}
    </div>
  );
};

const FieldMultipleSelect = props => {
  const { name } = props;

  return (
    <Field
      name={name}
      render={({ input, meta }) => <MultiSelectInput input={input} meta={meta} {...props} />}
    />
  );
};

export default FieldMultipleSelect;
